import React, { useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonShape,
  ButtonVariant,
} from '@vimily/bonjoro-ui-components-v2/Button';
import { computeTextColor } from '@vimily/bonjoro-ui-components-v2';
import cx from 'classnames';

export type NavActionProps = {
  onPreviousClick: () => Promise<void> | void | null;
  onNextClick: () => Promise<void> | void | null;
  previousDisabled: boolean;
  nextDisabled: boolean;
  className?: string;
  ctaColor?: string;
  ctaCornerRadius?: number;
  fontFamily?: string;
};

export default function NavAction({
  onPreviousClick,
  previousDisabled,
  onNextClick,
  nextDisabled,
  className,
  ctaColor,
  ctaCornerRadius = 4,
  fontFamily,
}: NavActionProps) {
  const { t } = useTranslation();
  const arrowButtonClass =
    'border-neutral-300 text-xl !p-0 w-[40px] h-[40px] bg-white disabled:bg-neutral-200';

  const buttonProps = useMemo(() => {
    const defaultStyle = {
      borderRadius: `${ctaCornerRadius}px`,
      fontFamily,
    };

    const props = {
      className:
        'ml-auto px-4 !h-10 !py-1.5 w-auto md:min-w-28 md:w-auto text-sm',
      variant: ButtonVariant.Primary,
      shape: ButtonShape.Round,
      onClick: onNextClick,
      disabled: nextDisabled,
      style: defaultStyle,
    };

    if (!nextDisabled) {
      return {
        ...props,
        style: {
          ...defaultStyle,
          borderColor: ctaColor,
          backgroundColor: ctaColor,
          color: computeTextColor(ctaColor),
        },
      };
    }

    return props;
  }, [onNextClick, nextDisabled, ctaCornerRadius, fontFamily, ctaColor]);

  return (
    <div
      className={cx(
        className,
        'flex z-10 bottom-0 left-0 w-full mt-auto relative bg-black/50 py-2.5 px-4'
      )}
    >
      <Button
        className={`${arrowButtonClass} rounded-r-none`}
        variant={ButtonVariant.Info}
        shape={ButtonShape.Round}
        onClick={onPreviousClick}
        disabled={previousDisabled}
      >
        <FontAwesomeIcon icon={faChevronUp} />
      </Button>
      <Button
        className={`${arrowButtonClass} rounded-l-none`}
        variant={ButtonVariant.Info}
        shape={ButtonShape.Round}
        onClick={onNextClick}
        disabled={nextDisabled}
      >
        <FontAwesomeIcon icon={faChevronDown} />
      </Button>
      <Button {...buttonProps}>{t('testimonial:response.next')}</Button>
    </div>
  );
}
